/**
 * Readers for the token-usage and context-window facts copilotd reports over
 * AHP: per-turn `UsageInfo` on the chat channel, and the
 * `copilot.usageInfo` snapshot on `SessionState._meta`.
 */

import type {ChatState, UsageInfo} from '@microsoft/agent-host-protocol'

export const ACTION_CHAT_USAGE = 'chat/usage'
/** Older turns arriving from pagination change the set the totals sum over. */
export const ACTION_CHAT_TURNS_LOADED = 'chat/turnsLoaded'

const META_KEY_USAGE_DETAIL = 'copilot.usageDetail'
const META_KEY_SESSION_USAGE_INFO = 'copilot.usageInfo'

/** Nano-AI units per AI credit. */
const NANO_AIU_PER_CREDIT = 1_000_000_000

/** Token counts accumulated across every turn the session has reported. */
export interface RelayTokenUsage {
  input: number
  output: number
  /** Prompt-cache reads plus writes. */
  cached: number
  reasoning: number
  /**
   * Spend in nano-AI units; divide by 1e9 for AI credits. `undefined` when no
   * model call reported cost, which is distinct from a reported zero.
   */
  nanoAiu?: number
  /**
   * True when the chat still has unloaded older turns, so these totals are a
   * lower bound rather than the whole session.
   */
  partial?: boolean
}

/** How much of the model's context window the session currently occupies. */
export interface RelayContextUsage {
  current: number
  limit: number
  system?: number
  tools?: number
  conversation?: number
  messages?: number
}

export interface RelaySessionUsage {
  tokens?: RelayTokenUsage
  context?: RelayContextUsage
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function positiveCount(value: unknown): number | undefined {
  const parsed = count(value)
  return parsed > 0 ? parsed : undefined
}

function record(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined
}

interface TokenTotals {
  input: number
  output: number
  cached: number
  reasoning: number
  nanoAiu: number
  /** Whether any turn carried a `UsageInfo` at all. */
  reported: boolean
  /** Whether any model call reported a cost, even a zero one. */
  costReported: boolean
}

/**
 * `UsageInfo` is a per-turn *cumulative* snapshot (the host re-emits the running
 * total on every model call), so session totals are the sum over turns rather
 * than over reports.
 */
function addTurnUsage(total: TokenTotals, usage: UsageInfo): void {
  total.reported = true
  total.input += count(usage.inputTokens)
  total.output += count(usage.outputTokens)
  total.cached += count(usage.cacheReadTokens)

  const detail = record(usage._meta?.[META_KEY_USAGE_DETAIL])
  if (!detail) return
  total.cached += count(detail['cacheWriteTokens'])
  total.reasoning += count(detail['reasoningTokens'])

  const nanoAiu = record(detail['copilotUsage'])?.['totalNanoAiu']
  if (typeof nanoAiu === 'number' && Number.isFinite(nanoAiu)) {
    total.costReported = true
    total.nanoAiu += nanoAiu
  }
}

/**
 * Session token totals folded from the chat state, or `undefined` when the host
 * has reported no usage at all — callers should suppress the surface rather
 * than render zeroes. A turn that reports an explicit zero still counts as
 * reported.
 *
 * `state.turns` holds only the loaded window when the chat is paginated, so the
 * result is flagged `partial` while older turns remain unloaded.
 */
export function sumTokenUsage(state: ChatState | undefined): RelayTokenUsage | undefined {
  if (!state) return undefined

  const total: TokenTotals = {
    input: 0,
    output: 0,
    cached: 0,
    reasoning: 0,
    nanoAiu: 0,
    reported: false,
    costReported: false,
  }
  for (const turn of state.turns ?? []) {
    if (turn.usage) addTurnUsage(total, turn.usage)
  }
  // A turn keeps its usage when it completes, so only fold the active turn in
  // while it is still absent from the history.
  const activeTurn = state.activeTurn
  if (activeTurn?.usage && !(state.turns ?? []).some(turn => turn.id === activeTurn.id)) {
    addTurnUsage(total, activeTurn.usage)
  }

  if (!total.reported) return undefined
  return {
    input: total.input,
    output: total.output,
    cached: total.cached,
    reasoning: total.reasoning,
    ...(total.costReported ? {nanoAiu: total.nanoAiu} : {}),
    ...(state.turnsNextCursor ? {partial: true} : {}),
  }
}

/**
 * Context-window occupancy from the `copilot.usageInfo` snapshot on
 * `SessionState._meta`, or `undefined` until the host reports a token limit.
 */
export function readContextUsage(meta: Record<string, unknown> | undefined): RelayContextUsage | undefined {
  const usage = record(meta?.[META_KEY_SESSION_USAGE_INFO])
  if (!usage) return undefined

  const limit = count(usage['tokenLimit'])
  if (limit <= 0) return undefined

  return {
    current: count(usage['currentTokens']),
    limit,
    ...withDefined('system', positiveCount(usage['systemTokens'])),
    ...withDefined('tools', positiveCount(usage['toolDefinitionsTokens'])),
    ...withDefined('conversation', positiveCount(usage['conversationTokens'])),
    ...withDefined('messages', positiveCount(usage['messagesLength'])),
  }
}

function withDefined<K extends string>(key: K, value: number | undefined): Partial<Record<K, number>> {
  return value === undefined ? {} : ({[key]: value} as Record<K, number>)
}

export function tokenUsageEquals(a: RelayTokenUsage | undefined, b: RelayTokenUsage | undefined): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return (
    a.input === b.input &&
    a.output === b.output &&
    a.cached === b.cached &&
    a.reasoning === b.reasoning &&
    a.nanoAiu === b.nanoAiu &&
    a.partial === b.partial
  )
}

export function contextUsageEquals(a: RelayContextUsage | undefined, b: RelayContextUsage | undefined): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return (
    a.current === b.current &&
    a.limit === b.limit &&
    a.system === b.system &&
    a.tools === b.tools &&
    a.conversation === b.conversation &&
    a.messages === b.messages
  )
}

/** Fraction of the context window in use, clamped to 0–1. */
export function contextUsageRatio(context: RelayContextUsage): number {
  if (context.limit <= 0) return 0
  return Math.min(Math.max(context.current / context.limit, 0), 1)
}

/** AI credits for a nano-AIU total. */
export function toAiCredits(nanoAiu: number): number {
  return nanoAiu / NANO_AIU_PER_CREDIT
}
